<script>
    import { createEventDispatcher } from "svelte";
    import { Section, Button } from "../../lib/ui";
    import ImagePicker from "../chat-builder/ImagePicker.svelte";
    import { botAssetFolder } from "../chat-builder/imageAssetApi.js";
    import { BUILTIN_SKILL_CHOICES, CHARACTER_STATS, CHARACTER_SLOT_KINDS, CHARACTER_SET_STEPS, newCharacter } from "./gameSlots.js";
    import AssetSetBatch from "./AssetSetBatch.svelte";

    export let bot;
    /** 저작된 캐릭터 목록. 비어 있으면 게임은 내장 기본 1명으로 논다. */
    export let characters = [];
    /** assetId → URL. 캐릭터 카드 썸네일용(저장하지 않는다). */
    export let urls = {};
    /** [스킬] 탭에서 저작한 스킬 목록. 캐릭터가 그중 하나를 골라 쓴다. */
    export let skillItems = [];

    // 저작한 스킬이 없으면 런타임이 내장 세트로 논다 — 고를 목록도 그걸 보여줘야 거짓말이 안 된다.
    $: skillChoices = skillItems.length > 0 ? skillItems : BUILTIN_SKILL_CHOICES;

    const dispatch = createEventDispatcher();

    let pickerOpen = false;
    let pickerTarget = null; // { index, field }

    const emit = (next) => dispatch("change", next);

    const updateAt = (index, patch) =>
        emit(characters.map((c, i) => (i === index ? { ...c, ...patch } : c)));

    const updateStat = (index, key, value) =>
        emit(
            characters.map((c, i) =>
                i === index ? { ...c, stats: { ...(c.stats || {}), [key]: value } } : c,
            ),
        );

    const resetStat = (index, key) =>
        emit(
            characters.map((c, i) => {
                if (i !== index) return c;
                const stats = { ...(c.stats || {}) };
                delete stats[key];
                return { ...c, stats };
            }),
        );

    const addCharacter = () => emit([...characters, newCharacter(characters.length)]);

    const removeCharacter = (index) => emit(characters.filter((_, i) => i !== index));

    const move = (index, dir) => {
        const to = index + dir;
        if (to < 0 || to >= characters.length) return;
        const next = [...characters];
        [next[index], next[to]] = [next[to], next[index]];
        emit(next);
    };

    const openPicker = (index, field) => {
        pickerTarget = { index, field };
        pickerOpen = true;
    };

    const onPick = (e) => {
        const { assetId, url } = e.detail;
        pickerOpen = false;
        if (!pickerTarget) return;
        const { index, field } = pickerTarget;
        pickerTarget = null;
        dispatch("assetUrl", { assetId, url });
        updateAt(index, { [field]: assetId });
    };

    const clearSlot = (index, field) => updateAt(index, { [field]: null });

    // 일괄 생성 결과를 그 캐릭터 슬롯에 꽂는다. 레시피의 slot 이 캐릭터 필드명과 같게 맞춰져 있다
    // (CHARACTER_SET_STEPS 의 playerAssetId/bulletAssetId).
    const onBatchGenerated = (index) => (e) => {
        const { slot, assetId, url } = e.detail;
        dispatch("assetUrl", { assetId, url });
        updateAt(index, { [slot]: assetId });
    };

    const statValue = (c, stat) => {
        const v = c.stats?.[stat.key];
        return typeof v === "number" && Number.isFinite(v) ? v : stat.def;
    };
    const isStatChanged = (c, stat) => statValue(c, stat) !== stat.def;
    const fmtStat = (stat, v) =>
        stat.kind === "delta" ? (v > 0 ? `+${v}` : String(v)) : `×${v.toFixed(1)}`;

    // ⚠️ 가리키던 스킬이 [스킬] 탭에서 지워졌을 수 있다. 런타임은 목록의 첫 번째로 떨어지는데,
    //    빌더가 아무 말 없이 "없음"이라고만 하면 저작자는 무엇이 나올지 모른다.
    const skillLabel = (id) => {
        const found = skillChoices.find((s) => s.id === id);
        if (found) return found.name || "(이름 없음)";
        return `지워진 스킬 → '${skillChoices[0]?.name || "첫 번째"}' 로 대체`;
    };
    $: freeCount = characters.filter((c) => (Number(c.price) || 0) <= 0).length;
</script>

<Section
    title="캐릭터"
    description="플레이어가 고르는 기체입니다. 캐릭터마다 그림·스킬·능력치가 다르고, 게임에서 번 코인으로 삽니다."
>
    <p class="tw-mb-2 tw-text-[13px] tw-leading-relaxed tw-text-muted-foreground">
        가격이 <strong class="tw-text-foreground">0</strong>인 캐릭터는 처음부터 쓸 수 있습니다.
        코인은 게임 중에 떨어지는 것을 주워 모으고, <strong class="tw-text-foreground">점수와는 다른 축</strong>입니다
        (점수는 기록으로만 남고 줄지 않습니다).
    </p>
    <p class="tw-text-[13px] tw-leading-relaxed tw-text-muted-foreground">
        능력치는 절대값이 아니라 <strong class="tw-text-foreground">[난이도] 탭에서 정한 값에 얹히는 보정</strong>입니다.
        난이도를 바꾸면 캐릭터들도 같이 따라옵니다.
    </p>

    {#if characters.length === 0}
        <div class="tw-mt-4 tw-rounded-lg tw-border tw-border-dashed tw-border-border tw-bg-muted/40 tw-p-5">
            <div class="tw-mb-1 tw-text-sm tw-font-bold tw-text-foreground">아직 만든 캐릭터가 없어요</div>
            <div class="tw-text-[13px] tw-leading-relaxed tw-text-muted-foreground">
                지금은 내장 기본 기체 1명으로 게임이 돌아갑니다. 하나라도 만들면 그때부터는
                <strong class="tw-text-foreground">여기 만든 것만</strong> 나옵니다.
            </div>
        </div>
    {:else if freeCount === 0}
        <div class="tw-mt-4 tw-text-xs tw-text-amber-600 dark:tw-text-amber-400">
            가격이 0인 캐릭터가 하나도 없습니다. 아무도 게임을 시작할 수 없어요 — 하나는 0으로 두세요.
        </div>
    {/if}
</Section>

{#each characters as c, i (c.id)}
    <Section>
        <div class="tw-mb-4 tw-flex tw-flex-wrap tw-items-center tw-gap-2">
            <input
                class="tw-h-9 tw-min-w-0 tw-flex-1 tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-3 tw-text-sm tw-text-foreground"
                type="text"
                placeholder="캐릭터 이름 (예: 붉은 매)"
                value={c.name || ""}
                on:change={(e) => updateAt(i, { name: e.target.value })}
            />
            <button
                type="button"
                class="tw-h-9 tw-w-9 tw-cursor-pointer tw-rounded-md tw-border tw-border-border tw-bg-background tw-text-xs tw-text-muted-foreground hover:tw-text-foreground"
                title="위로" on:click={() => move(i, -1)}>↑</button
            >
            <button
                type="button"
                class="tw-h-9 tw-w-9 tw-cursor-pointer tw-rounded-md tw-border tw-border-border tw-bg-background tw-text-xs tw-text-muted-foreground hover:tw-text-foreground"
                title="아래로" on:click={() => move(i, 1)}>↓</button
            >
            <button
                type="button"
                class="tw-h-9 tw-cursor-pointer tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-3 tw-text-xs tw-text-muted-foreground hover:tw-border-destructive hover:tw-text-destructive"
                on:click={() => removeCharacter(i)}>삭제</button
            >
        </div>

        <div class="tw-mb-4">
            <AssetSetBatch
                chatId={bot.id}
                steps={CHARACTER_SET_STEPS}
                folderPath={botAssetFolder(bot)}
                scopeLabel={`'${c.name || "이 캐릭터"}' 의 플레이어·탄환`}
                on:generated={onBatchGenerated(i)}
            />
        </div>

        <div class="tw-flex tw-flex-col tw-gap-5 lg:tw-flex-row lg:tw-gap-6">
            <!-- 그림 -->
            <div class="tw-flex tw-shrink-0 tw-gap-4">
                {#each CHARACTER_SLOT_KINDS as kind (kind.field)}
                    <div class="tw-flex tw-w-[108px] tw-flex-col tw-items-center tw-gap-1">
                        <button
                            type="button"
                            class="char-thumb {c[kind.field] && urls[c[kind.field]]
                                ? 'tw-border-border'
                                : 'tw-border-dashed tw-border-border tw-bg-muted'}"
                            on:click={() => openPicker(i, kind.field)}
                        >
                            {#if c[kind.field] && urls[c[kind.field]]}
                                <img crossorigin="anonymous" class="tw-max-h-full tw-max-w-full tw-object-contain" src={urls[c[kind.field]]} alt="" />
                            {:else}
                                <span class="tw-text-2xl tw-text-muted-foreground">+</span>
                            {/if}
                        </button>
                        <div class="tw-text-[13px] tw-font-semibold tw-text-foreground">{kind.label}</div>
                        {#if c[kind.field]}
                            <button
                                type="button"
                                class="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-text-[11px] tw-text-muted-foreground tw-underline"
                                on:click={() => clearSlot(i, kind.field)}>기본으로</button
                            >
                        {:else}
                            <div class="tw-text-center tw-text-[11px] tw-leading-tight tw-text-muted-foreground">
                                {kind.hint}
                            </div>
                        {/if}
                    </div>
                {/each}
            </div>

            <!-- 가격 · 스킬 · 능력치 -->
            <div class="tw-min-w-0 tw-flex-1">
                <div class="tw-mb-4 tw-flex tw-flex-wrap tw-gap-4">
                    <label class="tw-flex tw-flex-col tw-gap-1.5">
                        <span class="tw-text-[13px] tw-font-semibold tw-text-foreground">가격 (코인)</span>
                        <input
                            class="tw-h-9 tw-w-[140px] tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-3 tw-text-sm tw-text-foreground"
                            type="number"
                            min="0"
                            step="10"
                            value={c.price ?? 0}
                            on:change={(e) => updateAt(i, { price: Math.max(0, Math.floor(Number(e.target.value) || 0)) })}
                        />
                        <span class="tw-text-xs tw-text-muted-foreground">
                            {(Number(c.price) || 0) <= 0 ? "처음부터 쓸 수 있어요" : "코인을 모아야 열립니다"}
                        </span>
                    </label>

                    <label class="tw-flex tw-flex-col tw-gap-1.5">
                        <span class="tw-text-[13px] tw-font-semibold tw-text-foreground">스킬</span>
                        <select
                            class="tw-h-9 tw-w-[180px] tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-2 tw-text-sm tw-text-foreground"
                            value={skillChoices.some((s) => s.id === c.skill) ? c.skill : skillChoices[0]?.id}
                            on:change={(e) => updateAt(i, { skill: e.target.value })}
                        >
                            {#each skillChoices as s (s.id)}
                                <option value={s.id}>{s.name || "(이름 없음)"}</option>
                            {/each}
                        </select>
                        <span class="tw-text-xs tw-text-muted-foreground">
                            {skillItems.length > 0 ? "화면 오른쪽 아래 버튼" : "[스킬] 탭에서 만들면 여기에 나옵니다"}
                        </span>
                    </label>
                </div>

                <div class="tw-flex tw-flex-col tw-gap-3">
                    {#each CHARACTER_STATS as stat (stat.key)}
                        <div>
                            <div class="tw-mb-1 tw-flex tw-items-center tw-justify-between tw-gap-2">
                                <span class="tw-text-[13px] tw-text-foreground">{stat.label}</span>
                                <span class="tw-flex tw-items-center tw-gap-2 tw-text-[13px] tw-font-bold {isStatChanged(c, stat) ? 'tw-text-primary' : 'tw-text-muted-foreground'}">
                                    {fmtStat(stat, statValue(c, stat))}
                                    {#if isStatChanged(c, stat)}
                                        <button
                                            type="button"
                                            class="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-text-[11px] tw-font-normal tw-text-muted-foreground tw-underline"
                                            on:click={() => resetStat(i, stat.key)}>되돌리기</button
                                        >
                                    {/if}
                                </span>
                            </div>
                            <input
                                class="tw-w-full"
                                style="accent-color: hsl(var(--sb-primary))"
                                type="range"
                                min={stat.min}
                                max={stat.max}
                                step={stat.step}
                                value={statValue(c, stat)}
                                on:change={(e) => updateStat(i, stat.key, Number(e.target.value))}
                            />
                            <div class="tw-mt-1 tw-text-[11px] tw-text-muted-foreground">{stat.hint}</div>
                        </div>
                    {/each}
                </div>

                <div class="tw-mt-3 tw-text-xs tw-text-muted-foreground">
                    스킬 <strong class="tw-text-foreground">{skillLabel(c.skill)}</strong> ·
                    {(Number(c.price) || 0) <= 0 ? "기본 제공" : `${c.price} 코인`}
                </div>
            </div>
        </div>
    </Section>
{/each}

<div>
    <Button variant="outline" on:click={addCharacter}>+ 새 캐릭터 만들기</Button>
</div>

<ImagePicker
    bind:open={pickerOpen}
    chatId={bot.id}
    contextFolder={botAssetFolder(bot)}
    mode="single"
    currentAssetId={pickerTarget ? characters[pickerTarget.index]?.[pickerTarget.field] : null}
    on:select={onPick}
/>

<style>
    /* 투명 PNG 가 대부분이라 밝은 바탕에선 안 보인다 — 게임 화면과 비슷한 어두운 바탕에 둔다. */
    .char-thumb {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 108px;
        height: 108px;
        padding: 0;
        border-width: 1px;
        border-style: solid;
        border-radius: 12px;
        background: #0d1430;
        cursor: pointer;
        overflow: hidden;
    }

    .char-thumb:hover {
        border-color: hsl(var(--sb-primary));
    }
</style>
